import { createColumnHelper } from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { InterestSourcePill } from 'components/interest-source-pill';
import { formatNumber } from 'utils/number';
import { TractNRI } from 'components/nri-table/types';
import {
  InterestCell,
  OverAllocationWarning,
} from 'components/nri-table/over-allocation-warning';
import { useKeyedRows } from './hooks';
import {
  type DataTableRow,
  NRI_PRECISION,
  STATIC_TABLE_CONFIG,
  renderLink,
} from './utils';

interface SlotsTableProps {
  rows: TractNRI[];
  isLoading: boolean;
}

type SlotsTableRow = DataTableRow<TractNRI>;

/**
 * Row-key strategy — defined at module scope so `useKeyedRows`'s memo
 * doesn't bust each render.
 *
 * @param {TractNRI} row - Source row
 * @param {number} idx - Position in the array
 * @return {string} Stable row key
 */
const getRowKey = (row: TractNRI, idx: number): string =>
  row.slotId ?? `row-${idx}`;

const columnHelper = createColumnHelper<SlotsTableRow>();

// `accessor()` returns `AccessorKeyColumnDef<Row, FieldType>`, while
// `DataTableColumnDef<Row>` is invariant on the value generic
// (`unknown`). The trailing cast bridges that variance — required.
const COLUMNS = [
  columnHelper.display({
    id: 'slotId',
    header: 'Slot ID',
    cell: ({ row }) => renderLink(row.original.slotId, row.original.slotIdHref),
  }),
  columnHelper.display({
    id: 'unit',
    header: 'Unit',
    cell: ({ row }) => renderLink(row.original.unit, row.original.unitHref),
  }),
  columnHelper.accessor('totalSlotNri', {
    id: 'totalSlotNri',
    header: 'Total Unit NRI',
    meta: { type: 'decimal', format: { maxDecimalPlaces: NRI_PRECISION } },
    cell: ({ getValue, row }) => {
      const value = getValue();
      return (
        <InterestCell>
          {value !== null && value !== undefined
            ? formatNumber(value, NRI_PRECISION, NRI_PRECISION)
            : '—'}
          <OverAllocationWarning value={value} measure="NRI" />
          <InterestSourcePill
            wiSource={row.original.wiSource}
            nriCertifiedWorkspaceId={row.original.nriCertifiedWorkspaceId}
            interestEntityId={row.original.interestEntityId}
          />
        </InterestCell>
      );
    },
  }),
  columnHelper.accessor('totalSlotWi', {
    id: 'totalSlotWi',
    header: 'Total Unit WI',
    meta: { type: 'decimal', format: { maxDecimalPlaces: NRI_PRECISION } },
    cell: ({ getValue, row }) => {
      const value = getValue();
      return (
        <InterestCell>
          {value !== null && value !== undefined
            ? formatNumber(value, NRI_PRECISION, NRI_PRECISION)
            : '—'}
          <OverAllocationWarning value={value} measure="WI" />
          <InterestSourcePill
            wiSource={row.original.wiSource}
            nriCertifiedWorkspaceId={row.original.nriCertifiedWorkspaceId}
            interestEntityId={row.original.interestEntityId}
          />
        </InterestCell>
      );
    },
  }),
  columnHelper.accessor('unitSource', {
    id: 'unitSource',
    header: 'Unit Source',
    meta: { type: 'text' },
  }),
  columnHelper.accessor('unitDevelopmentType', {
    id: 'unitDevelopmentType',
    header: 'Unit Development Type',
    meta: { type: 'text' },
  }),
  columnHelper.accessor('wellName', {
    id: 'wellName',
    header: 'Well Name',
    meta: { type: 'text' },
  }),
  columnHelper.accessor('wellStatus', {
    id: 'wellStatus',
    header: 'Well Status',
    meta: { type: 'text' },
  }),
  columnHelper.accessor('operator', {
    id: 'operator',
    header: 'Operator',
    meta: { type: 'text' },
  }),
] as DataTableColumnDef<SlotsTableRow>[];

/**
 * Static, read-only `<DataTable>` for the Summary step's "Slots"
 * section — replaces the legacy `<SlotRollupNRITable>`'s expand /
 * sort / filter / CSV-export functionality with a simple presentation.
 * Slot ID and Unit cells render as Insights-page links when href fields
 * are present.
 *
 * @param {SlotsTableProps} props - The component props.
 * @return {JSX.Element} The slots table.
 */
export const SlotsTable = ({
  rows,
  isLoading,
}: SlotsTableProps): JSX.Element => {
  const keyedRows = useKeyedRows(rows, getRowKey);
  return (
    <DataTable<SlotsTableRow>
      data={keyedRows}
      columns={COLUMNS}
      getRowId={(row) => row.__key}
      isLoading={isLoading}
      config={STATIC_TABLE_CONFIG}
    />
  );
};
